import type { App } from '../../app.js';
import type { LocalGame } from '../../game/localGame.js';
import { h } from '../dom.js';
import { keyLabel } from '../../input.js';

/**
 * Flight school: teaches by doing. Steps watch the live game state and advance
 * when the pilot actually performs each technique.
 */

interface TutorialStep {
  title: string;
  body: (app: App) => string;
  /** Returns true when the step is complete. */
  check: (game: LocalGame, app: App) => boolean;
}

export interface TutorialController {
  update(dt: number): void;
  dispose(): void;
}

export function buildTutorialOverlay(app: App, game: LocalGame): TutorialController {
  const key = (action: Parameters<typeof appKey>[1]) => appKey(app, action);
  // Touch instructions when the on-screen controls are what the player is
  // (or will be) using: they've touched them, or this is a coarse-pointer device.
  const onTouch = () =>
    app.input.touch !== null &&
    (app.input.touch.used || (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches));

  const steps: TutorialStep[] = [
    {
      title: 'THROTTLE UP',
      body: () =>
        onTouch()
          ? 'Press and hold the left side of the screen to throttle. Reach 150 km/h.'
          : `Hold ${key('throttle')} (or right trigger) and get moving. Reach 150 km/h.`,
      check: (g) => (g.view.local?.speed ?? 0) * 3.6 > 150,
    },
    {
      title: 'STEERING',
      body: () =>
        onTouch()
          ? 'Keep holding and drag left or right to steer. Follow the road through the first sweeper.'
          : `Steer with ${key('steerLeft')}/${key('steerRight')} or the left stick. Follow the road through the first sweeper.`,
      check: (g) => (g.view.local?.raceTimeTicks ?? 0) > 60 * 12 || (g.view.local?.nextCheckpoint ?? 0) >= 3,
    },
    {
      title: 'DRIFT',
      body: () =>
        onTouch()
          ? 'Hold the DRIFT button while steering into a corner — the craft slides, and sliding charges your BOOST meter.'
          : `Hold ${key('drift')} (or a shoulder button) while steering into a corner — the craft slides, and sliding charges your BOOST meter.`,
      check: (g) => g.view.ships[0]?.drifting === true && (g.view.local?.speed ?? 0) > 25,
    },
    {
      title: 'BOOST',
      body: () =>
        onTouch()
          ? "Your meter (bottom right) is charged by drifting, drafting and pads. Straighten out and hold BOOST — it won't light while you're drifting."
          : `Your meter (bottom right) is charged by drifting, drafting and pads. Straighten out and fire it with ${key('boost')} — boost won't light while you're drifting.`,
      check: (g) => g.view.ships[0]?.boosting === true,
    },
    {
      title: 'BOOST PADS',
      body: () => 'The glowing chevrons on the deck are boost pads — run one over.',
      check: (g) => (g.view.ships[0]?.speed ?? 0) > 88,
    },
    {
      title: 'RECOVERY',
      body: () =>
        onTouch()
          ? 'If you ever leave the road, tap RESET to return to the last gate. Try it now — it costs time, so save it for disasters.'
          : `If you ever leave the road, ${key('respawn')} resets you to the last gate. Try it now — it costs time, so save it for disasters.`,
      check: (g) => g.view.ships[0]?.respawning === true,
    },
    {
      title: 'THE CANAL JUMP',
      body: () =>
        onTouch()
          ? 'Ahead the deck simply ends. Drag your drive finger up or down to level your nose and commit — speed is safety.'
          : 'Ahead the deck simply ends. Keep your nose level with pitch (I/K or right stick) and commit — speed is safety.',
      check: (g) => (g.view.local?.lap ?? 1) >= 2 || (g.view.local?.nextCheckpoint ?? 0) >= 13,
    },
    {
      title: 'GRADUATION LAP',
      body: () => 'Finish the remaining laps clean. The league is watching.',
      check: (g) => g.view.local?.finished === true,
    },
  ];

  let stepIndex = 0;
  let doneTimer = 0;

  const box = h(
    'div',
    {
      id: 'tutorial-box',
      style: `position: absolute; left: 50%; transform: translateX(-50%); bottom: 15vh; max-width: min(560px, 92vw);
              background: var(--panel); border: 1px solid var(--line-bright); border-left: 3px solid var(--accent);
              padding: 16px 24px; pointer-events: none; z-index: 40; text-align: center;`,
    },
    h('div', { class: 'h2', style: 'color: var(--accent); margin-bottom: 6px;', text: '' }),
    h('div', { style: 'font-size: 14px; line-height: 1.5; color: var(--text);', text: '' })
  );
  document.getElementById('ui-root')?.appendChild(box);
  const titleEl = box.children[0] as HTMLElement;
  const bodyEl = box.children[1] as HTMLElement;

  let shownTouchMode = onTouch();
  const showStep = () => {
    const step = steps[stepIndex];
    if (!step) return;
    shownTouchMode = onTouch();
    titleEl.textContent = `FLIGHT SCHOOL ${stepIndex + 1}/${steps.length} — ${step.title}`;
    bodyEl.textContent = step.body(app);
    box.style.opacity = '1';
  };
  showStep();

  return {
    update(_dt: number) {
      const step = steps[stepIndex];
      if (!step) return;
      // A touch-capable laptop may flip modes mid-run (first tap on the screen
      // controls) — refresh the instructions to match what they're holding.
      if (doneTimer <= 0 && onTouch() !== shownTouchMode) showStep();
      if (doneTimer > 0) {
        doneTimer -= _dt;
        if (doneTimer <= 0) {
          stepIndex++;
          if (stepIndex >= steps.length) {
            box.remove();
            app.settings.tutorialDone = true;
            app.saveAll();
          } else {
            showStep();
          }
        }
        return;
      }
      if (step.check(game, app)) {
        titleEl.textContent = `✔ ${step.title}`;
        app.audio.play('checkpoint');
        doneTimer = 1.2;
      }
    },
    dispose() {
      box.remove();
    },
  };
}

function appKey(app: App, action: keyof App['settings']['bindings']): string {
  const keys = app.settings.bindings[action]?.keys ?? [];
  return keys.length ? keyLabel(keys[0]) : '?';
}
